import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ConfirmComponent } from '../../../shared/components';

import { CommentService } from '../../../core/services/comment.service';
import { ToastService } from '../../../core/services/toast.service';
import { Store } from '@ngxs/store';
import { UserProfile } from '../../../core/models';
import { ValidationService } from '../../../core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { userImage } from '../../../core/helpers';

@Component({
    selector: 'gs-event-comments',
    templateUrl: './event-comments.component.html',
    standalone: false
})
export class EventCommentsComponent implements OnInit, OnDestroy {
  @Input() eventId: number;
  @Input() eventUserId: number;
  @Input() comments: any[];
  @ViewChild('commentForm') commentForm!: ElementRef;

  activeCommentId: number;
  userId: number;
  userImage: string;

  form: FormGroup;

  get isEventOwner(): boolean {
    return this.eventUserId == this.userId;
  }

  get activeComment(): any {
    return this.activeCommentId && this.comments.find(x => x.id == this.activeCommentId);
  }

  get actionDescription(): string {
    return this.activeCommentId ? `Raspunde lui ${this.activeComment.userName}` : 'Lasa un mesaj';
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private commentService: CommentService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private toastService: ToastService,
    private route: Router,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private store: Store,
    public vs: ValidationService
  ) {
    this.form = this.formBuilder.group({
      content: ['', [Validators.required, Validators.maxLength(500)]]
    });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.userId = user?.id;
          this.userImage = userImage(user?.image);
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onReplyClick(id) {
    this.activeCommentId = id;
    this.scrollToForm();
  }

  onCancelClick() {
    this.form.reset();
    this.activeCommentId = null;
  }

  onCommentSubmit() {
    if (!this.form.valid) {
      this.vs.triggerValidation(this.form);
      return;
    }

    const model = {
      ...this.form.value,
      eventId: this.eventId
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.activeComment ? this.commentService.createReply(this.activeCommentId, model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe((data: any) => {
          const comment = this.comments.find(x => x.id == this.activeCommentId);
          comment.replies = comment.replies || [];
          comment.replies.unshift({
            id: data.id,
            commentId: data.commentId,
            eventId: data.eventId,
            userImage: userImage(data.userImage),
            content: data.content,
            submittedDate: data.submittedDate,
            userName: data.userName,
            userId: data.userId
          });

          this.onCancelClick();
        })
        : this.commentService.createComment(model)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe((data: any) => {
            this.comments.unshift({
              id: data.id,
              content: data.content,
              eventId: data.eventId,
              submittedDate: data.submittedDate,
              userId: data.userId,
              userImage: userImage(data.userImage),
              userName: data.userName
            });

            this.onCancelClick();
          })
    );
  }

  onDeleteReplyClick(commentId, replyId) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Confirma stergerea';
    modalRef.componentInstance.description = `Datele asociate comentariului vor fi permanent sterse si nu pot fi recuperate.`;
    modalRef.componentInstance.acceptLabel = 'Sterge';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done.subscribe(result => {
        if (result === 'accept') {
          this.ngxUiLoaderService.start();
          this.subscriptions.push(
            this.commentService.deleteReply(commentId, replyId)
              .pipe(
                catchError(e => {
                  this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
                  return throwError(e);
                }),
                finalize(() => {
                  this.ngxUiLoaderService.stop();
                })
              )
              .subscribe(() => {
                const comment = this.comments.find(x => x.id == commentId);
                comment.replies = comment.replies.filter(r => r.id !== replyId);
                this.toastService.show(
                  'Succes',
                  'Comentariul a fost sters.',
                  'success'
                );
              })
          );
        }
      })
    );
  }

  onDeleteCommentClick(commentId: number) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Confirma stergerea';
    modalRef.componentInstance.description = `Datele asociate comentariului vor fi permanent sterse si nu pot fi recuperate.`;
    modalRef.componentInstance.acceptLabel = 'Sterge';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done.subscribe(result => {
        if (result === 'accept') {
          this.ngxUiLoaderService.start();
          this.subscriptions.push(
            this.commentService.deleteComment(commentId)
              .pipe(
                catchError(e => {
                  this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
                  return throwError(e);
                }),
                finalize(() => {
                  this.ngxUiLoaderService.stop();
                })
              )
              .subscribe(() => {
                this.comments = this.comments.filter(x => x.id !== commentId);
                this.toastService.show(
                  'Succes',
                  'Comentariul a fost sters.',
                  'success'
                );
              })
          );
        }
      })
    );
  }

  scrollToForm() {
    if (this.commentForm) {
      this.commentForm.nativeElement.scrollIntoView({
        block: 'center',
        inline: 'start',
        behavior: 'smooth'
      });
    }
  }
}
